<template>
	<!-- Level 0: Program Selection -->
	<v-container class="toggle animated" v-show="level === 0" fluid>
		<v-row v-if="loadingPrograms" class="fill-height">
			<v-col class="d-flex justify-center mt-16">
				<v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
			</v-col>
		</v-row>
		<v-row v-else-if="programs.length === 0" class="fill-height">
			<v-col class="d-flex justify-center mt-16">
				<v-card align="center" class="mt-16" elevation="0">
					<v-icon size="200">mdi-note-alert</v-icon>
					<v-card-text class="text-button">No Active Programs Found.</v-card-text>
				</v-card>
			</v-col>
		</v-row>
		<v-row v-else>
			<v-col v-for="prog in programs" :key="prog.id" cols="12" xl="4" md="4">
				<v-card @click="selectProgram(prog)" class="pa-2 d-flex flex-column fill-height" elevation="3">
					<v-card-text>
						<div class="text-h6 font-weight-black">{{ prog.abbrev }}</div>
						<div class="text-caption text-grey">{{ prog.name }}</div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>

	<!-- Level 1: Course Selection -->
	<v-container class="toggle animated" v-show="level === 1" fluid>
		<div class="d-flex flex-column flex-md-row justify-start align-left justify-md-space-between align-md-center mb-4 ga-2">
			<v-btn
				prepend-icon="mdi-chevron-left"
				@click="level = 0"
				class="rounded-lg"
				color="grey-darken-1"
				height="40px"
				elevation="2">
				Back
			</v-btn>
			<div class="font-weight-bold text-h6">
				{{ selectedProgram ? selectedProgram.abbrev + ' – Select a Course' : '' }}
			</div>
		</div>
		<v-row v-if="loadingCourses">
			<v-col class="d-flex justify-center mt-8">
				<v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
			</v-col>
		</v-row>
		<v-row v-else-if="courses.length === 0">
			<v-col class="d-flex justify-center mt-8">
				<v-card align="center" elevation="0">
					<v-icon size="120">mdi-book-open-blank-variant-outline</v-icon>
					<v-card-text class="text-button">No Courses Found for this Program.</v-card-text>
				</v-card>
			</v-col>
		</v-row>
        <div v-else>
            <v-row>
                <v-col cols="12" md="5">
                <v-text-field
                        v-model="search"
                        variant="outlined"
                        prepend-inner-icon="mdi-magnify"
                        label="Search"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        color="primary">
                    </v-text-field>
                </v-col>
            </v-row>
            <v-row>
				<v-col v-for="course in filteredCourses" :key="course.id" cols="12" xl="4" md="4">
                    <v-card @click="selectCourse(course)" class="pa-2 d-flex flex-column fill-height" elevation="3">
                        <v-card-text>
                            <div class="text-subtitle-1 font-weight-black">{{ course.code }}</div>
                            <div class="text-body-2 text-grey-darken-1 mt-1">{{ course.title }}</div>
                            <!-- <div class="d-flex ga-2 mt-2">
                                <v-chip size="x-small" variant="outlined" color="primary">
                                    {{ course.unit }} unit{{ course.unit !== 1 ? 's' : '' }}
                                </v-chip>
                                <v-chip v-if="course.hours_lecture" size="x-small" variant="outlined" color="blue">
                                    {{ course.hours_lecture }}h Lec
                                </v-chip>
                                <v-chip v-if="course.hours_lab" size="x-small" variant="outlined" color="orange">
                                    {{ course.hours_lab }}h Lab
                                </v-chip>
                            </div> -->
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>
	</v-container>

	<!-- Level 2: Course Outcomes -->
	<v-container class="toggle animated" v-show="level === 2" fluid>
		<div class="d-flex flex-column flex-md-row justify-start align-left justify-md-space-between align-md-center mb-4 ga-2">
			<v-btn
				prepend-icon="mdi-chevron-left"
				@click="level = 1"
				class="rounded-lg"
				color="grey-darken-1"
				height="40px"
				elevation="2">
				Back
			</v-btn>
			<div class="font-weight-bold text-h6">
				{{ cloPageTitle }}
			</div>
		</div>
		<CourseOutcomesDetails ref="CourseOutcomesDetails" />
	</v-container>
</template>

<script>
import { defineComponent } from 'vue'
import CourseOutcomesDetails from '../Sub-Parent/CourseOutcomesDetails.vue'

export default defineComponent({
	components: {
		CourseOutcomesDetails,
	},
	data() {
		return {
			level: 0,
			loadingPrograms: false,
			loadingCourses: false,
			search: null,
			programs: [],
			courses: [],
			selectedProgram: null,
			selectedCourse: null,
		}
	},
	computed: {
		filteredCourses() {
			if (!this.search) return this.courses

			const query = this.search.toLowerCase().trim()
			return this.courses.filter(course => {
				const code = (course.code || '').toLowerCase()
				const title = (course.title || '').toLowerCase()
				return code.includes(query) || title.includes(query)
			})
		},
		cloPageTitle() {
			if (!this.selectedCourse || !this.selectedProgram) return ''
			return this.selectedProgram.abbrev + ' – ' + this.selectedCourse.code + ' Course Learning Outcomes'
		}
	},
	methods: {
		async init() {
			this.loadingPrograms = true
			let res = await this.axiosCall(
				'/programs/findAllProgramByInstitute/' + this.$store.state.user.office.id,
				'get', null, true
			)
			if (res && res.data) {
				this.programs = res.data
			}
			this.loadingPrograms = false
		},
		async selectProgram(program) {
			this.selectedProgram = program
			this.level = 1
			this.loadingCourses = true
			this.search = null
			this.courses = []
			const [coursesRes, loadsRes] = await Promise.all([
				this.axiosCall('/course/findAllActiveCourseByProgram/' + program.id, 'get', null, true),
				this.axiosCall('/faculty-load', 'get', null, true),
			])

			if (coursesRes && coursesRes.data) {
				const semesterId = Number(this.$store.state.semester_schoolyear?.id)
				const loadCourseCodes = new Set(
					(loadsRes && Array.isArray(loadsRes.data) ? loadsRes.data : [])
						.filter((load) => Number(load.program_id) === Number(program.id))
						.filter((load) => Number(load.semester_schoolyear_id) === semesterId)
						.filter((load) => load.status === true)
						.map((load) => String(load.code || '').trim().toUpperCase())
						.filter((code) => !!code),
				)

				this.courses = (coursesRes.data || []).filter((course) =>
					loadCourseCodes.has(String(course.code || '').trim().toUpperCase()),
				)
			}
			this.loadingCourses = false
		},
		selectCourse(course) {
			this.selectedCourse = course
			this.level = 2
			this.$nextTick(() => {
				this.$refs.CourseOutcomesDetails.initData(course, this.selectedProgram)
			})
		},
	},
	mounted() {
		this.init()
	},
})
</script>

<style scoped>
</style>
 